import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Upload, PhoneIcon, X, CheckCircle, AlertCircle, LoaderIcon, SendIcon } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { CVFormModal } from './CVFormModal';
import { submitForm } from '../utils/submitForm';
function ConsultationModal({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    t,
    language
  } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';else document.body.style.overflow = '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);
  const resetForm = () => {
    setName('');
    setEmail('');
    setPhone('');
    setComment('');
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setStatus('idle');
    const fd = new FormData();
    fd.append('name', name);
    fd.append('email', email);
    fd.append('phone', phone);
    fd.append('message', `[Consultation Request]${comment ? ` ${comment}` : ''}`);
    try {
      const result = await submitForm('service', fd, language);
      if (result.ok) {
        setStatus('success');
        resetForm();
        setTimeout(() => {
          setStatus('idle');
          onClose();
        }, 2500);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };
  if (!isOpen) return null;
  return ReactDOM.createPortal(<div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-366">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl my-8" role="dialog" aria-modal="true" data-id="element-367">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white rounded-t-2xl border-b border-gray-100 px-6 py-4 flex items-center justify-between" data-id="element-368">
          <div data-id="element-369">
            <h2 className="text-xl font-bold text-[var(--color-text-dark)]" data-id="element-370">
              {t('floating.consultTitle')}
            </h2>
            <p className="text-sm text-[var(--color-text-light)] mt-0.5" data-id="element-371">
              {t('floating.consultDesc')}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0" aria-label="Close" data-id="element-372">
            <X className="w-5 h-5 text-gray-500" data-id="element-373" />
          </button>
        </div>

        {/* Success overlay */}
        {status === 'success' && <div className="absolute inset-0 z-20 bg-white/95 rounded-2xl flex flex-col items-center justify-center gap-3" data-id="element-374">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center" data-id="element-375">
              <CheckCircle className="w-8 h-8 text-green-600" data-id="element-376" />
            </div>
            <p className="text-lg font-semibold text-[var(--color-text-dark)]" data-id="element-377">
              {t('floating.success')}
            </p>
            <p className="text-sm text-[var(--color-text-light)]" data-id="element-378">
              {t('floating.successDesc')}
            </p>
          </div>}

        <div className="p-6" data-id="element-379">
          {/* Social Links */}
          <div className="grid grid-cols-2 gap-3 mb-6" data-id="element-380">
            <a href="https://t.me/elvora_consulting" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors" data-id="element-381">
              <div className="w-9 h-9 rounded-lg bg-[var(--color-tan)]/10 flex items-center justify-center flex-shrink-0" data-id="element-382">
                <svg className="w-5 h-5 text-[var(--color-tan)]" viewBox="0 0 24 24" fill="currentColor" data-id="element-383">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-384" />
                </svg>
              </div>
              <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-385">
                Telegram
              </span>
            </a>

            <a href="https://api.whatsapp.com/send/?phone=380731356233" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors" data-id="element-386">
              <div className="w-9 h-9 rounded-lg bg-[var(--color-tan)]/10 flex items-center justify-center flex-shrink-0" data-id="element-387">
                <svg className="w-5 h-5 text-[var(--color-tan)]" viewBox="0 0 24 24" fill="currentColor" data-id="element-388">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" data-id="element-389" />
                </svg>
              </div>
              <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-390">
                WhatsApp
              </span>
            </a>

            <a href="https://www.linkedin.com/company/111293896/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors" data-id="element-391">
              <div className="w-9 h-9 rounded-lg bg-[var(--color-tan)]/10 flex items-center justify-center flex-shrink-0" data-id="element-392">
                <svg className="w-5 h-5 text-[var(--color-tan)]" viewBox="0 0 24 24" fill="currentColor" data-id="element-393">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-394" />
                </svg>
              </div>
              <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-395">
                LinkedIn
              </span>
            </a>

            <a href="mailto:info@elvora.consulting" className="flex items-center gap-3 px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors" data-id="element-396">
              <div className="w-9 h-9 rounded-lg bg-[var(--color-tan)]/10 flex items-center justify-center flex-shrink-0" data-id="element-397">
                <svg className="w-5 h-5 text-[var(--color-tan)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" data-id="element-398">
                  <rect x="2" y="4" width="20" height="16" rx="2" data-id="element-399" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" data-id="element-400" />
                </svg>
              </div>
              <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-401">
                Email
              </span>
            </a>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-6" data-id="element-402">
            <div className="flex-1 h-px bg-gray-200" data-id="element-403" />
            <span className="text-xs text-[var(--color-text-light)] whitespace-nowrap" data-id="element-404">
              {t('floating.orSendMessage')}
            </span>
            <div className="flex-1 h-px bg-gray-200" data-id="element-405" />
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-406">
            <div data-id="element-407">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-408">
                {t('floating.labelName')}{' '}
                <span className="text-red-500" data-id="element-409">*</span>
              </label>
              <input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-410" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-411">
              <div data-id="element-412">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-413">
                  {t('floating.labelEmail')}{' '}
                  <span className="text-red-500" data-id="element-414">*</span>
                </label>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-415" />
              </div>
              <div data-id="element-416">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-417">
                  {t('floating.labelPhone')}{' '}
                  <span className="text-red-500" data-id="element-418">*</span>
                </label>
                <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 234 567 8900" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors placeholder:text-gray-400" data-id="element-419" />
              </div>
            </div>

            <div data-id="element-420">
              <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-421">
                {t('floating.labelComment')}
              </label>
              <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3} placeholder={t('floating.commentPlaceholder')} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors resize-none placeholder:text-gray-400" data-id="element-422" />
            </div>

            {status === 'error' && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-id="element-423">
                <AlertCircle className="w-4 h-4 flex-shrink-0" data-id="element-424" />
                <span data-id="element-425">{t('floating.error')}</span>
              </div>}

            <button type="submit" disabled={submitting} className={`w-full px-6 py-3.5 text-white text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2 ${!submitting ? 'bg-[var(--color-tan)] hover:opacity-90' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-426">
              {submitting ? <>
                  <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-427" />
                  {t('floating.submitting')}
                </> : <>
                  <SendIcon className="w-4 h-4" data-id="element-428" />
                  {t('floating.submitBtn')}
                </>}
            </button>

            <p className="text-xs text-[var(--color-text-light)] text-center" data-id="element-429">
              {t('floating.disclaimer')}
            </p>
          </form>
        </div>
      </div>
    </div>, document.body);
}
export function FloatingActions() {
  const {
    t
  } = useLanguage();
  const [cvModalOpen, setCvModalOpen] = useState(false);
  const [consultModalOpen, setConsultModalOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      // Show floating buttons only after user scrolls past the hero area
      const threshold = window.innerHeight * 0.6;
      setVisible(window.scrollY > threshold);
    };
    handleScroll(); // check initial position
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const anyModalOpen = cvModalOpen || consultModalOpen;
  return <>
      {/* Desktop floating buttons — positioned at opposite edges */}
      <button onClick={() => setCvModalOpen(true)} className="hidden md:flex fixed bottom-6 left-8 z-40 items-center gap-2.5 px-6 py-3.5 bg-[var(--color-tan)] text-white text-sm font-medium rounded-full shadow-lg shadow-[var(--color-tan)]/25 hover:opacity-90 hover:shadow-xl transition-all" style={{
      opacity: visible && !anyModalOpen ? 1 : 0,
      transform: visible && !anyModalOpen ? 'translateY(0)' : 'translateY(20px)',
      transition: 'opacity 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1)',
      pointerEvents: visible && !anyModalOpen ? 'auto' : 'none'
    }} data-id="element-430">
        <Upload className="w-4 h-4" data-id="element-431" />
        {t('floating.submitCV')}
      </button>

      <button onClick={() => setConsultModalOpen(true)} className="hidden md:flex fixed bottom-6 right-8 z-40 items-center gap-2.5 px-6 py-3.5 bg-[var(--color-tan)] text-white text-sm font-medium rounded-full shadow-lg shadow-[var(--color-tan)]/25 hover:opacity-90 hover:shadow-xl transition-all" style={{
      opacity: visible && !anyModalOpen ? 1 : 0,
      transform: visible && !anyModalOpen ? 'translateY(0)' : 'translateY(20px)',
      transition: 'opacity 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1)',
      pointerEvents: visible && !anyModalOpen ? 'auto' : 'none'
    }} data-id="element-432">
        <PhoneIcon className="w-4 h-4" data-id="element-433" />
        {t('floating.consultation')}
      </button>

      {/* Mobile sticky bottom bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]" style={{
      opacity: visible && !anyModalOpen ? 1 : 0,
      transform: visible && !anyModalOpen ? 'translateY(0)' : 'translateY(100%)',
      transition: 'opacity 0.4s cubic-bezier(0.16,1,0.3,1), transform 0.4s cubic-bezier(0.16,1,0.3,1)',
      pointerEvents: visible && !anyModalOpen ? 'auto' : 'none'
    }} data-id="element-434">
        <div className="flex gap-3 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]" data-id="element-435">
          <button onClick={() => setCvModalOpen(true)} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-xl active:opacity-90 transition-opacity" data-id="element-436">
            <Upload className="w-4 h-4" data-id="element-437" />
            {t('floating.submitCV')}
          </button>

          <button onClick={() => setConsultModalOpen(true)} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-xl active:opacity-90 transition-opacity" data-id="element-438">
            <PhoneIcon className="w-4 h-4" data-id="element-439" />
            {t('floating.consultation')}
          </button>
        </div>
      </div>

      <CVFormModal isOpen={cvModalOpen} onClose={() => setCvModalOpen(false)} mode="seafarer" data-id="element-440" />

      <ConsultationModal isOpen={consultModalOpen} onClose={() => setConsultModalOpen(false)} data-id="element-441" />
    </>;
}